import React, { FC, MouseEventHandler, ReactNode } from "react";
import { Link } from "react-router-dom";
import styles from "./Button.module.css";

export type Variant = "primary" | "secondary" | "tertiary" | "submit" | "cancel" | "text" | "special";

interface ButtonProps {
    children: ReactNode;
    type?: "button" | "submit" | "reset";
    variant?: Variant;
    size?: "small" | "medium" | "large";
    disabled?: boolean;
    onClick?: MouseEventHandler<HTMLButtonElement>;
    to?: string;
    href?: string;
    target?: "_blank" | "_self" | "_parent" | "_top";
}

const Button: FC<ButtonProps> = ({
    children,
    type = "button",
    variant = "primary",
    size = "medium",
    disabled = false,
    onClick,
    to,
    href,
    target = "_self",
}) => {
    const ariaLabel = typeof children === "string" ? children : undefined;

    if (to) {
        return (
            <Link
                to={to}
                className={`${styles.button} ${styles[variant]} ${styles[size]} ${disabled ? styles.disabled : ""}`}
                aria-label={ariaLabel}
                aria-disabled={disabled}
            >
                {children}
            </Link>
        );
    }

    if (href) {
        return (
            <a
                href={href}
                target={target}
                className={`${styles.button} ${styles[variant]} ${styles[size]} ${disabled ? styles.disabled : ""}`}
                aria-label={ariaLabel}
                aria-disabled={disabled}
                rel="noopener noreferrer"
            >
                {children}
            </a>
        );
    }

    return (
        <button
            type={type}
            onClick={onClick}
            className={`${styles.button} ${styles[variant]} ${styles[size]} ${disabled ? styles.disabled : ""}`}
            disabled={disabled}
            aria-label={ariaLabel}
            aria-disabled={disabled}
        >
            {children}
        </button>
    );
};

export default Button;
